import type { ChangeEventHandler, FocusEventHandler } from 'react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { Field, SectionHeading, fieldCls } from './formFields';
import PhoneRow from './PhoneRow';
import PartyLocationDropdown from './PartyLocationDropdown';
import {
  GENDERS,
  RELIGIONS,
  RESERVATIONS,
  TITLES,
  toSelectOptions,
  type UserFormValues,
} from './types';

type SetField = (field: string, value: unknown) => void;

interface Props {
  values: UserFormValues;
  setFieldValue: SetField;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errorOf: (name: keyof UserFormValues) => string | undefined;
  organization?: number;
  designationOptions: { value: string; label: string }[];
}

/** Party Person (party) create form — flat layout (Figma 1384-1448). */
export default function PartyPersonForm({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errorOf,
  organization,
  designationOptions,
}: Props) {
  return (
    <div className="flex flex-col gap-6">
      {/* Primary Information */}
      <div className="flex flex-col gap-4">
        <SectionHeading>Primary Information</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="w-full md:w-[120px]">
            <Field label="Title">
              <Select
                className="w-full"
                placeholder="Shri"
                value={values.title || undefined}
                onChange={(v) => setFieldValue('title', v ?? '')}
                options={toSelectOptions(TITLES)}
                allowClear
              />
            </Field>
          </div>
          <Field label="First Name" htmlFor="firstName" required error={errorOf('firstName')}>
            <input
              id="firstName"
              name="firstName"
              placeholder="e.g. Rajesh"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('firstName'))}
            />
          </Field>
          <Field label="Middle Name" htmlFor="middleName">
            <input
              id="middleName"
              name="middleName"
              placeholder="e.g. Kumar"
              value={values.middleName}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
          <Field label="Last Name" htmlFor="lastName" required error={errorOf('lastName')}>
            <input
              id="lastName"
              name="lastName"
              placeholder="e.g. Sharma"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('lastName'))}
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Email Address" htmlFor="email" error={errorOf('email')}>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="rajesh.k@chunaav.gov.in"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('email'))}
            />
          </Field>
          <Field label="Party Hierarchy Level" error={errorOf('location')}>
            <PartyLocationDropdown
              organization={organization}
              value={values.location}
              invalid={Boolean(errorOf('location'))}
              onChange={(key) => setFieldValue('location', key)}
            />
          </Field>
        </div>

        <PhoneRow
          contact={values.contact}
          landline={values.landline}
          landlineExt={values.landlineExt}
          whatsapp={values.whatsapp}
          whatsappIndex={values.whatsappIndex}
          contactError={errorOf('contact')}
          setFieldValue={setFieldValue}
        />
      </div>

      {/* Demographic Attributes + Party Role */}
      <div className="flex flex-col gap-4 border-t border-[#f1f5f9] pt-6">
        <SectionHeading>Demographic Attributes</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Date of Birth" required error={errorOf('dob')}>
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              status={errorOf('dob') ? 'error' : undefined}
              value={values.dob ? dayjs(values.dob) : null}
              onChange={(d) => setFieldValue('dob', d ? d.format('YYYY-MM-DD') : '')}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          </Field>
          <Field label="Gender" required error={errorOf('gender')}>
            <Select
              className="w-full"
              placeholder="Select Gender"
              status={errorOf('gender') ? 'error' : undefined}
              value={values.gender || undefined}
              onChange={(v) => setFieldValue('gender', v ?? '')}
              options={toSelectOptions(GENDERS)}
              allowClear
            />
          </Field>
          <Field label="Religion">
            <Select
              className="w-full"
              placeholder="Select Religion"
              value={values.religion || undefined}
              onChange={(v) => setFieldValue('religion', v ?? '')}
              options={toSelectOptions(RELIGIONS)}
              allowClear
            />
          </Field>
          <Field label="Caste / Sub-Caste">
            <input
              name="caste"
              placeholder="Community Name"
              value={values.caste}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
          <Field label="Reservation Category">
            <Select
              className="w-full"
              placeholder="General"
              value={values.reservation || undefined}
              onChange={(v) => setFieldValue('reservation', v ?? '')}
              options={toSelectOptions(RESERVATIONS)}
              allowClear
            />
          </Field>
        </div>

        <Field label="Party Role">
          <Select
            mode="multiple"
            className="w-full"
            placeholder="Select Party Role(s)"
            showSearch
            optionFilterProp="label"
            value={values.designation}
            onChange={(v) => setFieldValue('designation', v)}
            options={designationOptions}
          />
        </Field>
      </div>
    </div>
  );
}
